import * as React from 'react';
import {
  Form,
  Row,
  Col,
  Input,
  Icon,
  Select,
  Button,
  Switch,
  Modal,
  Checkbox,
  Tooltip,
  Card,
} from 'antd';
import { FormComponentProps } from 'antd/lib/form';
import { RouteComponentProps } from 'react-router-dom';
import FormControl from 'components/Forms/FormControl';
import MaskedInput from 'components/Forms/MaskedInput';
import { withRouter } from 'react-router';
import { CreditCardRegistrationFormHandler } from './CreditCardRegistrationFormHandler';
import {
  ReferenceValueDto,
  IReferenceValueDto,
  BeginRegisterCreditCardResponse,
  IBeginRegisterCreditCardRequest,
} from 'Core/Api/Payment/Api';
import TokenUtility from 'Core/TokenUtility';
import AuthContext from 'Context/AuthContext';
import { validate } from '@babel/types';
import { element } from 'prop-types';
import FormUtility from 'Core/FormUtility';
import ValidationUtility from 'Core/ValidationUtility';
import { LOGROCKET_IGNORE_CLASS } from 'components/LogRocketErrorMonitor/LogRocketErrorMonitor';
import TerritorySelector from './TerritorySelector';
import NotificationUtility from 'Core/NotificationUtility';
import ThreeDSecureView from 'components/Payment/ThreeDSecureView';
import { SubmitButton } from 'components/SubmitButton';
import styled from 'styled-components';

export interface Props extends FormComponentProps, RouteComponentProps {
  children?: React.ReactNode;
  cancelButton(): any;
  registered(): any;
}

export interface State {
  cardTypes: IReferenceValueDto[];
  cardCurrencies: IReferenceValueDto[];
  verificationResponse?: BeginRegisterCreditCardResponse;
  confirmCardModalConfig?: any;
}

class BaseCreditCardRegistrationForm extends React.Component<Props, State> {
  static contextType = AuthContext;
  context!: React.ContextType<typeof AuthContext>;
  creditCardRegistrationFormHandler: CreditCardRegistrationFormHandler;
  VerificationModal = React.createRef<Modal>();
  confirmCardModal: { destroy: () => void; } | undefined = undefined;

  constructor(props: Props) {
    super(props);
    this.creditCardRegistrationFormHandler = new CreditCardRegistrationFormHandler(props);
    this.onFormSubmit = this.onFormSubmit.bind(this);
    this.onCancel = this.onCancel.bind(this);
    // this.getCardType = this.getCardType.bind(this);
    // this.getCardCurrency = this.getCardCurrency.bind(this);

    this.state = {
      cardTypes: [],
      cardCurrencies: [],
    };
  }

  componentWillUnmount() {
    this.confirmCardModal?.destroy();
  }

  componentDidMount() {
    this.setState({});
    this.getCardType();
    this.getCardCurrency();
  }

  onCancel() {
    this.props.cancelButton();
  }
  onRegistered = () => {
    this.props.registered();
  };

  getCardType() {
    this.creditCardRegistrationFormHandler
      .GetCreditCardTypes()
      .then(type => {
        if (type !== undefined) {
          this.setState({ cardTypes: type });
        }
      })
      .catch(x => { });
  }

  getCardCurrency() {

    this.creditCardRegistrationFormHandler
      .GetCreditCardCurrency()
      .then(currency => {
        if (currency !== undefined) {
          this.setState({ cardCurrencies: currency });
        }
      })
      .catch(x => { });
  }

  onSuccessfulVerification = () => {
    const confirmCardPrompt = (
      <p style={{ textAlign: 'center' }}>
        To complete the Registration & Verification process, please retrieve the <br />
        amount charged to your card by contacting your bank and then return <br />
        to the Register & Verify screen.
      </p>);

    let confirmCardModalConfig = {
      title: 'Card Details Entry Complete',
      content: confirmCardPrompt,
      okText: 'I understand',
      onOk: () => {
        NotificationUtility.success('Card Registration Successful', 'Card Details Entry Completed Successfully.', 10, true);
        this.onRegistered();
      }
    };

    this.setState({ confirmCardModalConfig });

  };

  onFailedVerification = error => {
    NotificationUtility.error('Payment Verification Failed', error?.message ?? "A Failure occured when attempting to verify the payment.", 10, true);
    this.setState({ verificationResponse: undefined });
  };

  onFormSubmit(event: React.FormEvent) {
    event.preventDefault();

    this.props.form.validateFieldsAndScroll((error, values: IBeginRegisterCreditCardRequest) => {
      if (!error) {
        // set expiry month & year
        if (values.cardDetails !== undefined) {
          const expiryParts = FormUtility.getExpiryDateParts(values.cardDetails.expiry);
          values.cardDetails.expiryMonth = expiryParts?.month!;
          values.cardDetails.expiryYear = expiryParts?.year!;
        }

        // remove spacing in card number from masking
        if (values !== undefined) {
          const cardNum = values.cardDetails.cardNumber;
          const cardNumSplit = cardNum!.split(' ');
          const cardNumber = cardNumSplit.join('');
          values.cardDetails.cardNumber = cardNumber;
        }

        // set user id and email address
        if (this.context.data) {
          values.userId = this.context.data.id!;
          values.userEmailAddress = this.context.data.email!;
        }

        // Collect province/territory value outside of JM beacuase the select's tags mode places data in array format
        if (
          values.cardDetails.billingAddress.province &&
          values.cardDetails.billingAddress.countryCode !== 'JM'
        ) {
          values.cardDetails.billingAddress.province =
            values.cardDetails.billingAddress.province[0];
        }

        this.creditCardRegistrationFormHandler
          .RegisterCreditCard(values)
          .then(x => {
            this.setState({ verificationResponse: x as BeginRegisterCreditCardResponse });
          })
          .catch(x => {
            NotificationUtility.error('Card Registration Failed', x.message || x, 25, true);
          });
      }
    });
  }

  render() {
    const { getFieldDecorator } = this.props.form;
    const { form } = this.props;
    const { Option } = Select;

    const cardTypeSelector = (
      <Select showSearch={true} allowClear={true}>
        {this.state.cardTypes.map(type => (
          <Option key={type.id} value={type.id}>
            {type.description}
          </Option>
        ))}
      </Select>
    );

    const cardCurrencySelector = (
      <Select showSearch={true} allowClear={true}>
        {this.state.cardCurrencies.map(currency => (
          <Option key={currency.id} value={currency.id}>
            {currency.description}
          </Option>
        ))}
      </Select>
    );

    const CustomCheckbox = styled(Checkbox) <{ backgroundColor: any }>`
      ${props =>
        props.backgroundColor && `
          & .ant-checkbox .ant-checkbox-inner {
            background-color: ${props.backgroundColor};
            border-color: ${props.backgroundColor};
          }
          
        `}
    `;

    return (
      <>
        <Modal
          visible={!!this.state.confirmCardModalConfig}
          {...this.state.confirmCardModalConfig}
          destroyOnClose={true}>
          {this.state.confirmCardModalConfig?.content}
        </Modal>

        <Modal
          closable={false}
          visible={!!this.state.verificationResponse}
          ref={this.VerificationModal}
          destroyOnClose={true}
          footer={null}>
          <ThreeDSecureView
            onSuccess={this.onSuccessfulVerification}
            onFailure={this.onFailedVerification}
            verificationResponse={this.state.verificationResponse}
          />
        </Modal>

        <Row type="flex" justify="center">
          <Col span={22} offset={2}>
            <Form onSubmit={this.onFormSubmit} className={LOGROCKET_IGNORE_CLASS} layout="vertical">

              {/* Card Details */}
              <Card
                title="Card Details"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.0)',
                  borderRadius: '20px',
                  overflow: 'hidden'
                }}
                headStyle={{ backgroundColor: '#f8f8f8', border: 5 }}
              >

                <Row type="flex" align='bottom'>
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 24 }}
                    md={{ span: 16 }}
                    lg={{ span: 16 }}
                    xl={{ span: 16 }}>
                    <FormControl
                      getFieldDecorator={getFieldDecorator}
                      rules={[...ValidationUtility.getRequired('Please enter your Card Name')]}
                      name="cardDetails.cardName"
                      label="Name on Card">
                      <Input
                        prefix={<Icon type="user" style={{ color: 'rgba(0,0,0,.25)' }} />}
                        placeholder="Cardholder"
                      />
                    </FormControl>
                  </Col>
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 24 }}
                    md={{ span: 6, offset: 1 }}
                    lg={{ span: 6, offset: 1 }}
                    xl={{ span: 6, offset: 1 }}>
                    <FormControl
                      style={{ fontWeight: 'bold' }}
                      getFieldDecorator={getFieldDecorator}
                      name="saveTokenizeCard"
                      label={<Tooltip title="Saving card information will allow you to select a registered card at checkout instead of filling in the information again. You will still need to enter the CVV.">Save Card for faster Checkout&nbsp;<Icon type="info-circle" /></Tooltip>}
                      valuePropName="checked">

                      <CustomCheckbox backgroundColor="#a1a1a1"/> 

                    </FormControl>
                  </Col>
                </Row>

                {/* Credit  Card Type*/}
                <Row type="flex" align="top" gutter={10}>
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 24 }}
                    md={{ span: 15 }}
                    lg={{ span: 15 }}
                    xl={{ span: 15 }}>
                    <FormControl
                      getFieldDecorator={getFieldDecorator}
                      rules={[
                        ...ValidationUtility.getRequired('Please enter Card Number'),
                        ...ValidationUtility.getCreditCardNumber(
                          this.props.form,
                          'cardDetails.cardTypeId'
                        ),
                      ]}
                      name="cardDetails.cardNumber"
                      label="Card Number">
                      <MaskedInput
                        prefix={<Icon type="credit-card" style={{ color: 'rgba(0,0,0,.25)' }} />}
                        mask="9999 9999 9999 9999"
                        alwaysShowMask={true}
                        placeholder="Card Number"
                      />
                    </FormControl>
                  </Col>

                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 24 }}
                    md={{ span: 9 }}
                    lg={{ span: 9 }}
                    xl={{ span: 9 }}>
                    <FormControl
                      getFieldDecorator={getFieldDecorator}
                      rules={[
                        ...ValidationUtility.getRequired('Please select a Credit Card Type'),
                        ...ValidationUtility.validateOtherFields(form, 'cardDetails.cardNumber'),
                      ]}
                      name="cardDetails.cardTypeId"
                      label="Credit Card Type"
                    // initialValue='Visa'
                    >
                      {cardTypeSelector}
                    </FormControl>
                  </Col>
                </Row>

                <Row type="flex" align="top" gutter={10}>
                  {/* Card Currency */}
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 24 }}
                    md={{ span: 8 }}
                    lg={{ span: 8 }}
                    xl={{ span: 8 }}>
                    <FormControl
                      getFieldDecorator={getFieldDecorator}
                      rules={[...ValidationUtility.getRequired('Please select Card Currency')]}
                      name="cardDetails.cardCurrencyCode"
                      label="Card Currency">
                      {cardCurrencySelector}
                    </FormControl>
                  </Col>

                  {/* Expiry Date */}
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 24 }}
                    md={{ span: 9 }}
                    lg={{ span: 9 }}
                    xl={{ span: 9 }}>
                    <FormControl
                      getFieldDecorator={getFieldDecorator}
                      rules={[
                        ...ValidationUtility.getRequired('Please enter Card expiry date'),
                        ...ValidationUtility.getIsValidExpiryDate(),
                      ]}
                      name="cardDetails.expiry"
                      label="Expiry Date (MM/YY)">
                      <MaskedInput
                        prefix={<Icon type="card" style={{ color: 'rgba(0,0,0,.25)' }} />}
                        mask="99/99"
                        alwaysShowMask={true}
                        className="Sumperess"
                        placeholder="Card Number"
                      />
                    </FormControl>
                  </Col>

                  {/* CVV2/CVCV2 */}
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 24 }}
                    md={{ span: 7 }}
                    lg={{ span: 7 }}
                    xl={{ span: 7 }}>
                    <FormControl
                      getFieldDecorator={getFieldDecorator}
                      rules={[
                        ...ValidationUtility.getRequired('Please enter CVV2 Number'),
                        ...ValidationUtility.getPattern(
                          /^[0-9]{3}/,
                          'Please enter valid  CVV2 Number'
                        ),
                      ]}
                      name="cardDetails.cvv"
                      label="CVV2/CVC2">
                      <MaskedInput
                        prefix={<Icon type="card" style={{ color: 'rgba(0,0,0,.25)' }} />}
                        mask="999"
                        className="Sumperess"
                        placeholder="CVV2/CVC2"
                      />
                    </FormControl>
                  </Col>
                </Row>
              </Card>

              <p> <br /> </p>

              {/* Card Billing Details  */}
              <Card
                title="Card Billing Details"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.0)',
                  borderRadius: '20px',
                  overflow: 'hidden'
                }}
                headStyle={{ backgroundColor: '#f8f8f8' }}
              >

                <Row type="flex" align="top" gutter={10}>
                  {/* Card Holder's Name */}
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 24 }}
                    md={{ span: 8 }}
                    lg={{ span: 8 }}
                    xl={{ span: 8 }}>
                    <FormControl
                      getFieldDecorator={getFieldDecorator}
                      rules={[...ValidationUtility.getRequired('Please enter your First Name')]}
                      name="cardDetails.billingAddress.firstName"
                      label="First Name">
                      <Input
                        prefix={<Icon type="user" style={{ color: 'rgba(0,0,0,.25)' }} />}
                        placeholder="First Name"
                      />
                    </FormControl>
                  </Col>
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 24 }}
                    md={{ span: 8 }}
                    lg={{ span: 8 }}
                    xl={{ span: 8 }}>
                    <FormControl
                      getFieldDecorator={getFieldDecorator}
                      name="cardDetails.billingAddress.middleName"
                      label="Middle Name">
                      <Input
                        prefix={<Icon type="user" style={{ color: 'rgba(0,0,0,.25)' }} />}
                        placeholder="Middle Name"
                      />
                    </FormControl>
                  </Col>
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 24 }}
                    md={{ span: 8 }}
                    lg={{ span: 8 }}
                    xl={{ span: 8 }}>
                    <FormControl
                      getFieldDecorator={getFieldDecorator}
                      rules={[...ValidationUtility.getRequired('Please enter your Last Name')]}
                      name="cardDetails.billingAddress.lastName"
                      label="Last Name">
                      <Input
                        prefix={<Icon type="user" style={{ color: 'rgba(0,0,0,.25)' }} />}
                        placeholder="Last Name"
                      />
                    </FormControl>
                  </Col>
                </Row>
                {/* Address Line 1 */}
                <Row type="flex" align="middle">
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 24 }}
                    md={{ span: 24 }}
                    lg={{ span: 24 }}
                    xl={{ span: 24 }}>
                    <FormControl
                      getFieldDecorator={getFieldDecorator}
                      rules={[...ValidationUtility.getRequired('Please enter Address Line 1')]}
                      name="cardDetails.billingAddress.addressLine1"
                      label="Address Line 1">
                      <Input
                        prefix={<Icon type="pushpin" style={{ color: 'rgba(0,0,0,.25)' }} />}
                        placeholder="Address Line 1"
                      />
                    </FormControl>
                  </Col>
                </Row>

                {/* Address Line 2 */}
                <Row type="flex" align="middle">
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 24 }}
                    md={{ span: 24 }}
                    lg={{ span: 24 }}
                    xl={{ span: 24 }}>
                    <FormControl
                      getFieldDecorator={getFieldDecorator}
                      name="cardDetails.billingAddress.addressLine2"
                      label="Address Line 2">
                      <Input
                        prefix={<Icon type="pushpin" style={{ color: 'rgba(0,0,0,.25)' }} />}
                        placeholder="Address Line 2"
                      />
                    </FormControl>
                  </Col>
                </Row>

                <Row type="flex" align="top" gutter={10}>
                  {/* Country */}
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 24 }}
                    md={{ span: 8 }}
                    lg={{ span: 8 }}
                    xl={{ span: 8 }}>
                    <FormControl
                      getFieldDecorator={getFieldDecorator}
                      rules={[...ValidationUtility.getRequired('Please select a Country')]}
                      name="cardDetails.billingAddress.countryCode"
                      label="Country"
                      initialValue="JM">
                      {FormUtility.countrySelector()}
                    </FormControl>
                  </Col>

                  {/* City */}
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 24 }}
                    md={{ span: 8 }}
                    lg={{ span: 8 }}
                    xl={{ span: 8 }}>
                    <FormControl
                      getFieldDecorator={getFieldDecorator}
                      rules={[...ValidationUtility.getRequired('Please enter a City')]}
                      name="cardDetails.billingAddress.city"
                      label="City">
                      <Input
                        prefix={<Icon type="pushpin" style={{ color: 'rgba(0,0,0,.25)' }} />}
                        placeholder="City"
                      />
                    </FormControl>
                  </Col>

                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 24 }}
                    md={{ span: 8 }}
                    lg={{ span: 8 }}
                    xl={{ span: 8 }}>
                    {/* Parish -  STATE(USES STATE) IF USE/CANADA; PARISH(USES PROVINCE) FOR JAMAICA; PROVINCE FOR EVERWHERE ELSE*/}
                    <TerritorySelector
                      intialValue={''}
                      countryCode={
                        form.getFieldValue('cardDetails.billingAddress.countryCode')
                          ? form.getFieldValue('cardDetails.billingAddress.countryCode')
                          : 'JM'
                      }
                      formDecorator={getFieldDecorator}
                    />
                  </Col>
                </Row>

                <Row type="flex" align="top" gutter={10}>
                  {/* Zip/Postal Code */}
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 24 }}
                    md={{ span: 8 }}
                    lg={{ span: 8 }}
                    xl={{ span: 8 }}>
                    <FormControl
                      getFieldDecorator={getFieldDecorator}
                      name="cardDetails.billingAddress.zipOrPostalCode"
                      label="ZIP/Postal Code">
                      <Input
                        prefix={<Icon type="pushpin" style={{ color: 'rgba(0,0,0,.25)' }} />}
                        placeholder="ZIP/Postal Code"
                      />
                    </FormControl>
                  </Col>

                  {/* Mobile Number */}
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 24 }}
                    md={{ span: 8 }}
                    lg={{ span: 8 }}
                    xl={{ span: 8 }}>
                    <FormControl
                      getFieldDecorator={getFieldDecorator}
                      rules={[
                        ...ValidationUtility.getRequired('Please enter your Telephone Number'),
                        ...ValidationUtility.getPattern(
                          /[0-9]{3}-[0-9]{3}-[0-9]{4}/,
                          'Please enter valid Mobile Number'
                        ),
                      ]}
                      label="Mobile Number"
                      name="cardDetails.billingAddress.telephone">
                      <MaskedInput
                        prefix={<Icon type="phone" style={{ color: 'rgba(0,0,0,.25)' }} />}
                        mask="999-999-9999"
                        alwaysShowMask={true}
                        className="Sumperess"
                        placeholder="Mobile Number"
                      />
                    </FormControl>
                  </Col>
                </Row>
              </Card>

              <p> </p>

              <Row type='flex' gutter={10}>
                <Col
                  xs={{ span: 5 }}
                  sm={{ span: 5 }}
                  md={{ span: 5, offset: 6 }}
                  lg={{ span: 5, offset: 6 }}
                  xl={{ span: 5, offset: 6 }}>
                  <SubmitButton type="primary" htmlType="submit" className="login-form-button" style={{ textAlign: "center" }} loadingStyle={{ marginLeft: "-25px" }}>Submit Card Details</SubmitButton>
                </Col>
                <Col span={2} />
                <Col
                  xs={{ span: 5, offset: 1 }}
                  sm={{ span: 5, offset: 1 }}
                  md={{ span: 5, offset: 1 }}
                  lg={{ span: 5, offset: 1 }}
                  xl={{ span: 5, offset: 1 }}>
                  <Button onClick={this.onCancel} style={{ backgroundColor: "#FF6232", color: 'white' }} className="login-form-button">
                    Cancel
                  </Button>
                </Col>
              </Row>
            </Form>
          </Col>
          <Col span={2}>
          </Col>
        </Row>
      </>
    );
  }
}

const CreditCardRegistrationFormWithoutRouter = Form.create<Props>({
  name: 'UpdateUserProfileForm',
})(BaseCreditCardRegistrationForm);

const CreditCardRegistrationForm = withRouter(CreditCardRegistrationFormWithoutRouter);

export default CreditCardRegistrationForm;
